import { useTranslation } from 'react-i18next';
import {
  STORY_CARD_THEMES,
  TEXT_CARD_VOCAB_MAX,
  type NotebookStoryCard,
  type QuoteStoryCard,
  type StoryCard,
  type StoryCardTheme,
  type TextStoryCard,
  type VideoStoryCard,
} from '@org/model';
import {
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  TextArea,
  storyColors,
} from '@org/ui';
import {
  FieldPair,
  FormField,
  LocalizedField,
  MediaField,
  MediaSourceField,
  VocabEditor,
} from './story-form-fields';

// One form per card template. Each form edits the card it is given and hands
// back the whole updated card.

type CardFormProps<C extends StoryCard> = {
  card: C;
  onChange: (card: C) => void;
};

const THEME_SWATCH: Record<StoryCardTheme, string> = {
  ink: storyColors.black,
  red: storyColors.red,
  paper: storyColors.cream,
};

function TitleField<C extends StoryCard>({ card, onChange }: CardFormProps<C>) {
  const { t } = useTranslation('admin');
  return (
    <LocalizedField
      idBase={`card-${card.id}-title`}
      label={t('women.cards.fields.title')}
      hint={t('women.cards.fields.titleHint')}
      value={card.title}
      onChange={(title) => onChange({ ...card, title })}
    />
  );
}

function TextCardForm({ card, onChange }: CardFormProps<TextStoryCard>) {
  const { t } = useTranslation('admin');
  return (
    <div className="flex flex-col gap-4">
      <TitleField card={card} onChange={onChange} />
      <FieldPair>
        <FormField
          htmlFor={`card-${card.id}-big`}
          label={t('women.cards.fields.big')}
          hint={t('women.cards.fields.bigHint')}
        >
          <Input
            id={`card-${card.id}-big`}
            value={card.big ?? ''}
            onChange={(event) => onChange({ ...card, big: event.target.value })}
          />
        </FormField>
        <FormField label={t('women.cards.fields.theme')}>
          <Select value={card.theme} onValueChange={(theme) => onChange({ ...card, theme: theme as StoryCardTheme })}>
            <SelectTrigger className="w-full text-left">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {STORY_CARD_THEMES.map((theme) => (
                <SelectItem key={theme} value={theme}>
                  <span className="inline-flex items-center gap-2">
                    <span
                      aria-hidden="true"
                      className="inline-block h-4 w-4 shrink-0 rounded-full border border-border"
                      style={{ backgroundColor: THEME_SWATCH[theme] }}
                    />
                    {t(`women.cards.themes.${theme}`)}
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </FormField>
      </FieldPair>
      <LocalizedField
        idBase={`card-${card.id}-body`}
        label={t('women.cards.fields.body')}
        hint={t('women.cards.fields.bodyHint')}
        value={card.body}
        onChange={(body) => onChange({ ...card, body })}
        multiline
        rows={6}
      />
      <VocabEditor
        idBase={`card-${card.id}`}
        value={card.vocab}
        onChange={(vocab) => onChange({ ...card, vocab })}
        max={TEXT_CARD_VOCAB_MAX}
      />
    </div>
  );
}

function VideoCardForm({ card, onChange }: CardFormProps<VideoStoryCard>) {
  const { t } = useTranslation('admin');
  return (
    <div className="flex flex-col gap-4">
      <TitleField card={card} onChange={onChange} />
      <FormField label={t('women.cards.fields.video')}>
        <MediaSourceField
          idBase={`card-${card.id}-video`}
          kind="video"
          mode={card.source}
          onModeChange={(source) => onChange({ ...card, source })}
          url={card.url}
          onUrlChange={(url) => onChange({ ...card, url })}
          linkPlaceholder={t('women.cards.fields.videoLinkPlaceholder')}
          showReferenceWarning
        />
      </FormField>
      <FormField label={t('women.cards.fields.poster')} hint={t('women.cards.fields.posterHint')}>
        <MediaField
          idBase={`card-${card.id}-poster`}
          kind="image"
          url={card.posterUrl ?? ''}
          onUrlChange={(posterUrl) => onChange({ ...card, posterUrl })}
          showReferenceWarning
        />
      </FormField>
      <LocalizedField
        idBase={`card-${card.id}-caption`}
        label={t('women.cards.fields.caption')}
        value={card.caption}
        onChange={(caption) => onChange({ ...card, caption })}
      />
    </div>
  );
}

function NotebookCardForm({ card, onChange }: CardFormProps<NotebookStoryCard>) {
  const { t } = useTranslation('admin');
  return (
    <div className="flex flex-col gap-4">
      <TitleField card={card} onChange={onChange} />
      <LocalizedField
        idBase={`card-${card.id}-date`}
        label={t('women.cards.fields.date')}
        hint={t('women.cards.fields.dateHint')}
        value={card.date}
        onChange={(date) => onChange({ ...card, date })}
      />
      <FormField
        htmlFor={`card-${card.id}-original`}
        label={t('women.cards.fields.original')}
        hint={t('women.cards.fields.originalHint')}
      >
        <TextArea
          id={`card-${card.id}-original`}
          rows={5}
          value={card.original}
          onChange={(event) => onChange({ ...card, original: event.target.value })}
        />
      </FormField>
      <FormField label={t('women.cards.fields.originalLang')} className="sm:max-w-xs">
        <Select
          value={card.originalLang}
          onValueChange={(originalLang) => onChange({ ...card, originalLang: originalLang as 'en' | 'pt' })}
        >
          <SelectTrigger className="w-full text-left">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="en">{t('women.editor.langEnglish')}</SelectItem>
            <SelectItem value="pt">{t('women.editor.langPortuguese')}</SelectItem>
          </SelectContent>
        </Select>
      </FormField>
      <LocalizedField
        idBase={`card-${card.id}-source`}
        label={t('women.cards.fields.source')}
        hint={t('women.cards.fields.sourceHint')}
        value={card.source}
        onChange={(source) => onChange({ ...card, source })}
      />
      <LocalizedField
        idBase={`card-${card.id}-gloss`}
        label={t('women.cards.fields.gloss')}
        hint={t('women.cards.fields.glossHint')}
        value={card.gloss}
        onChange={(gloss) => onChange({ ...card, gloss })}
        multiline
        rows={4}
      />
      <VocabEditor
        idBase={`card-${card.id}`}
        value={card.vocab}
        onChange={(vocab) => onChange({ ...card, vocab })}
        max={TEXT_CARD_VOCAB_MAX}
      />
    </div>
  );
}

function QuoteCardForm({ card, onChange }: CardFormProps<QuoteStoryCard>) {
  const { t } = useTranslation('admin');
  return (
    <div className="flex flex-col gap-4">
      <p className="rounded-md bg-muted px-3 py-2 text-sm text-muted-foreground">
        {t('women.cards.fields.quoteNote')}
      </p>
      <TitleField card={card} onChange={onChange} />
      <LocalizedField
        idBase={`card-${card.id}-body`}
        label={t('women.cards.fields.quoteBody')}
        hint={t('women.cards.fields.quoteBodyHint')}
        value={card.body}
        onChange={(body) => onChange({ ...card, body })}
        multiline
        rows={3}
      />
    </div>
  );
}

/** The form of one card, picked by its type. */
export function StoryCardForm({ card, onChange }: CardFormProps<StoryCard>) {
  const { t } = useTranslation('admin');
  switch (card.type) {
    case 'text':
      return <TextCardForm card={card} onChange={onChange} />;
    case 'video':
      return <VideoCardForm card={card} onChange={onChange} />;
    case 'notebook':
      return <NotebookCardForm card={card} onChange={onChange} />;
    case 'quote':
      return <QuoteCardForm card={card} onChange={onChange} />;
    default:
      // A type this editor does not know (hand-edited JSON): edit it in the JSON tab.
      return <p className="text-sm text-muted-foreground">{t('women.cards.unknownType')}</p>;
  }
}
